import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

export const snakesLaddersTransitions = {
  4: 14,
  9: 31,
  17: 7,
  20: 38,
  28: 84,
  40: 59,
  51: 67,
  54: 34,
  62: 19,
  63: 81,
  64: 60,
  71: 91,
  87: 24,
  93: 73,
  95: 75,
  99: 78,
} as const;

const playerSchema = z.object({
  id: z.string(),
  color: z.enum(["blue", "coral", "yellow", "cyan"]).optional(),
  position: z.number().int().min(0).max(100),
  rolls: z.number().int().nonnegative(),
});

const moveSchema = z.object({
  nonce: z.number().int().positive(),
  playerId: z.string(),
  roll: z.number().int().min(1).max(6),
  from: z.number().int().min(0).max(100),
  landedAt: z.number().int().min(0).max(100),
  to: z.number().int().min(0).max(100),
  transport: z.enum(["snake", "ladder"]).nullable(),
  automatic: z.boolean().optional(),
  at: z.number(),
});

export const SNAKES_LADDERS_ROLL_TIME_SECONDS = 20;

export const snakesLaddersStateSchema = z.object({
  players: z.array(playerSchema).min(2).max(4),
  turnIndex: z.number().int().min(0).max(3),
  phase: z.enum(["playing", "finished"]),
  winnerId: z.string().nullable(),
  placementIds: z.array(z.string()).default([]),
  lastMove: moveSchema.nullable(),
  moveNonce: z.number().int().nonnegative(),
  rollTimeSeconds: z.number().int().min(10).max(120).default(SNAKES_LADDERS_ROLL_TIME_SECONDS),
  turnDeadlineAt: z.number().nullable().default(null),
  fastMode: z.boolean().default(false),
  round: z.number().int().positive(),
});

export const snakesLaddersEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("roll") }),
  z.object({ type: z.literal("rematch") }),
  z.object({ type: z.literal("set_fast_mode"), enabled: z.boolean() }),
]);

export const snakesLaddersSettingsSchema = z.object({
  exactFinish: z.boolean().default(true),
  fastMode: z.boolean().default(false),
});

export type SnakesLaddersState = z.infer<typeof snakesLaddersStateSchema>;
export type SnakesLaddersEvent = z.infer<typeof snakesLaddersEventSchema>;
export type SnakesLaddersSettings = z.infer<typeof snakesLaddersSettingsSchema>;

const snakesLaddersPlayerColors = ["blue", "coral", "yellow", "cyan"] as const;

function normalizedRandomIndex(
  length: number,
  randomInt?: (maximumExclusive: number) => number,
): number {
  if (length <= 1) return 0;
  const value = randomInt?.(length) ?? 0;
  return Math.abs(Math.trunc(value)) % length;
}

export function randomSnakesLaddersColors(
  count: number,
  randomInt?: (maximumExclusive: number) => number,
) {
  const colors = [...snakesLaddersPlayerColors];
  for (let index = colors.length - 1; index > 0; index -= 1) {
    const target = normalizedRandomIndex(index + 1, randomInt);
    [colors[index], colors[target]] = [
      colors[target] as (typeof colors)[number],
      colors[index] as (typeof colors)[number],
    ];
  }
  return colors.slice(0, Math.max(0, Math.min(count, colors.length)));
}

function nextTurn(state: SnakesLaddersState) {
  for (let offset = 1; offset <= state.players.length; offset += 1) {
    const candidate = (state.turnIndex + offset) % state.players.length;
    const player = state.players[candidate];
    if (player && player.position < 100 && !state.placementIds.includes(player.id))
      return candidate;
  }
  return state.turnIndex;
}

function resetPlayers(
  players: SnakesLaddersState["players"],
  randomInt?: (maximumExclusive: number) => number,
) {
  const colors = randomSnakesLaddersColors(players.length, randomInt);
  return players.map((player, index) => ({
    ...player,
    color: colors[index] ?? player.color ?? snakesLaddersPlayerColors[index],
    position: 0,
    rolls: 0,
  }));
}

export const snakesLaddersGame: TopzleGameDefinition<
  SnakesLaddersState,
  SnakesLaddersEvent,
  SnakesLaddersSettings
> = {
  id: "snakes-ladders",
  version: "1.0.0",
  name: "Snakes & Ladders",
  shortDescription: "Race to 100, climb bright ladders, and dodge the snakes.",
  longDescription:
    "A server-authoritative two-to-four player race with animated step movement, dramatic snake bites, ladder climbs, spectators, late joining, and instant bot play.",
  category: "board",
  icon: "route",
  cover: "snakes-ladders",
  minPlayers: 2,
  maxPlayers: 4,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "avatar_only"],
  preferredLayout: "game_fullscreen",
  stateSchema: snakesLaddersStateSchema,
  eventSchema: snakesLaddersEventSchema,
  settingsSchema: snakesLaddersSettingsSchema,
  createInitialState(context, settings) {
    const playerIds = context.playerIds.slice(0, 4);
    const colors = randomSnakesLaddersColors(playerIds.length, context.randomInt);
    return {
      players: playerIds.map((id, index) => ({
        id,
        color: colors[index] ?? snakesLaddersPlayerColors[index],
        position: 0,
        rolls: 0,
      })),
      turnIndex: 0,
      phase: "playing",
      winnerId: null,
      placementIds: [],
      lastMove: null,
      moveNonce: 0,
      rollTimeSeconds: SNAKES_LADDERS_ROLL_TIME_SECONDS,
      turnDeadlineAt: context.now + SNAKES_LADDERS_ROLL_TIME_SECONDS * 1_000,
      fastMode: settings.fastMode,
      round: 1,
    };
  },
  validateEvent(state, event, context) {
    if (event.type === "set_fast_mode") return { valid: true };
    if (event.type === "rematch")
      return {
        valid: state.phase === "finished",
        code: "game_active",
        message: "Finish this race first",
      };
    if (state.phase !== "playing")
      return { valid: false, code: "finished", message: "The race is complete" };
    if (state.players[state.turnIndex]?.id !== context.actorId)
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    if (event.type === "set_fast_mode") return { ...state, fastMode: event.enabled };
    if (event.type === "rematch")
      return {
        ...state,
        players: resetPlayers(state.players, context.randomInt),
        turnIndex: state.round % state.players.length,
        phase: "playing",
        winnerId: null,
        placementIds: [],
        lastMove: null,
        turnDeadlineAt: context.now + state.rollTimeSeconds * 1_000,
        round: state.round + 1,
      };
    const current = state.players[state.turnIndex];
    if (!current) return state;
    const roll = (context.randomInt?.(6) ?? 0) + 1;
    const attempted = current.position + roll;
    const landedAt = attempted <= 100 ? attempted : current.position;
    const transition = (snakesLaddersTransitions as Partial<Record<number, number>>)[landedAt];
    const to: number = transition ?? landedAt;
    const transport = transition
      ? transition > landedAt
        ? ("ladder" as const)
        : ("snake" as const)
      : null;
    const players = state.players.map((player) =>
      player.id === current.id ? { ...player, position: to, rolls: player.rolls + 1 } : player,
    );
    const placementIds =
      to === 100 && !state.placementIds.includes(current.id)
        ? [...state.placementIds, current.id]
        : state.placementIds;
    const winnerId = placementIds[0] ?? null;
    const remainingPlayers = players.filter(
      (player) => player.position < 100 && !placementIds.includes(player.id),
    );
    const finished = remainingPlayers.length <= 1;
    const nextState = { ...state, players, placementIds };
    return {
      ...state,
      players,
      turnIndex: finished ? state.turnIndex : nextTurn(nextState),
      phase: finished ? "finished" : "playing",
      winnerId,
      placementIds,
      moveNonce: state.moveNonce + 1,
      turnDeadlineAt: finished ? null : context.now + state.rollTimeSeconds * 1_000,
      lastMove: {
        nonce: state.moveNonce + 1,
        playerId: current.id,
        roll,
        from: current.position,
        landedAt,
        to,
        transport,
        at: context.now,
      },
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions(state, playerId) {
    return state.phase === "playing" && state.players[state.turnIndex]?.id === playerId
      ? [{ type: "roll" }]
      : [];
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished" || !state.winnerId) return null;
    return {
      status: "completed",
      winnerIds: [state.winnerId],
      placements: [
        ...state.placementIds,
        ...state.players
          .filter((player) => !state.placementIds.includes(player.id))
          .sort((left, right) => right.position - left.position)
          .map((player) => player.id),
      ],
      scores: Object.fromEntries(
        state.players.map((player) => [player.id, player.id === state.winnerId ? 1 : 0]),
      ),
      reason: "reached_square_100",
    };
  },
  async createBotAction() {
    return { type: "roll" };
  },
  getNextDeadline(state) {
    return state.phase === "playing" ? state.turnDeadlineAt : null;
  },
  handleDeadline(state, context) {
    if (
      state.phase !== "playing" ||
      state.turnDeadlineAt === null ||
      state.turnDeadlineAt > context.now
    )
      return state;
    const next = snakesLaddersGame.reduceEvent(state, { type: "roll" }, context);
    return {
      ...next,
      lastMove: next.lastMove ? { ...next.lastMove, automatic: true } : null,
    };
  },
  handlePlayerJoin(state, arrival, context) {
    if (
      state.phase !== "playing" ||
      state.players.length >= 4 ||
      state.players.some((player) => player.id === arrival.playerId)
    )
      return null;
    const occupiedColors = new Set(
      state.players.map(
        (player, index) => player.color ?? snakesLaddersPlayerColors[index] ?? "blue",
      ),
    );
    const availableColors = snakesLaddersPlayerColors.filter((color) => !occupiedColors.has(color));
    return {
      state: {
        ...state,
        players: [
          ...state.players,
          {
            id: arrival.playerId,
            color:
              availableColors[normalizedRandomIndex(availableColors.length, context.randomInt)] ??
              snakesLaddersPlayerColors[state.players.length],
            position: 0,
            rolls: 0,
          },
        ],
      },
    };
  },
  handlePlayerDeparture(state, departure) {
    const index = state.players.findIndex((player) => player.id === departure.playerId);
    if (index < 0) return null;
    if (departure.replacementPlayerId) {
      const replacementPlayerId = departure.replacementPlayerId;
      return {
        state: {
          ...state,
          players: state.players.map((player) =>
            player.id === departure.playerId ? { ...player, id: replacementPlayerId } : player,
          ),
          winnerId: state.winnerId === departure.playerId ? replacementPlayerId : state.winnerId,
          placementIds: state.placementIds.map((id) =>
            id === departure.playerId ? replacementPlayerId : id,
          ),
        },
        replacementPlayerId,
      };
    }
    if (state.players.length <= 2) return null;
    const players = state.players.filter((player) => player.id !== departure.playerId);
    const placementIds = state.placementIds.filter((id) => id !== departure.playerId);
    const unfinishedPlayers = players.filter(
      (player) => player.position < 100 && !placementIds.includes(player.id),
    );
    const finished = unfinishedPlayers.length <= 1;
    return {
      state: {
        ...state,
        players,
        phase: finished ? "finished" : state.phase,
        placementIds,
        turnDeadlineAt: finished ? null : state.turnDeadlineAt,
        turnIndex: finished
          ? Math.max(
              0,
              players.findIndex((player) => player.id === unfinishedPlayers[0]?.id),
            )
          : index < state.turnIndex
            ? state.turnIndex - 1
            : state.turnIndex >= players.length
              ? 0
              : state.turnIndex,
      },
    };
  },
};
